<template>
    <component
        :is="tag"
        ref="el"
        class="pb2-doc-table__cell"
        contenteditable="true"
        @input="onInput"
        @blur="onBlur"
        @keydown="onKeydown"
        @pointerdown="onPointerdown"
        @contextmenu="onContextmenu"
    />
</template>

<script setup lang="ts">
/**
 * One contenteditable table cell. The template deliberately renders no
 * text children — the DOM owns the content while typing, so Vue never
 * patches textContent under the caret. External matrix changes sync only
 * while the cell is not focused.
 */
import { onMounted, ref, watch } from 'vue';

const props = defineProps<{ tag: 'th' | 'td'; text: string }>();

const emit = defineEmits<{ commit: [text: string, final: boolean]; menu: [event: MouseEvent] }>();

const STREAM_DEBOUNCE_MS = 300;

const el = ref<HTMLElement | null>(null);
let timer: ReturnType<typeof setTimeout> | null = null;

const clearTimer = (): void => {
    if (timer !== null) {
        clearTimeout(timer);
        timer = null;
    }
};

const currentText = (): string => el.value?.textContent ?? '';

const onInput = (): void => {
    clearTimer();
    timer = setTimeout(() => emit('commit', currentText(), false), STREAM_DEBOUNCE_MS);
};

const onBlur = (): void => {
    clearTimer();
    emit('commit', currentText(), true);
};

const onKeydown = (event: KeyboardEvent): void => {
    event.stopPropagation();

    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        el.value?.blur();
    } else if (event.key === 'Escape') {
        event.preventDefault();
        clearTimer();

        if (el.value) {
            el.value.textContent = props.text;
        }

        el.value?.blur();
    }
};

// Right-click focuses a contenteditable before contextmenu fires, so the
// "was the user already editing here?" answer must be captured at
// pointerdown. An already-active cell keeps the native menu (spellcheck,
// paste) — parity with the canvas, where an active session preserves it.
let activeBeforePointerdown = false;

const onPointerdown = (event: PointerEvent): void => {
    if (event.button === 2) {
        activeBeforePointerdown = document.activeElement === el.value;
    }
};

const onContextmenu = (event: MouseEvent): void => {
    if (!activeBeforePointerdown) {
        emit('menu', event);
    }
};

onMounted(() => {
    if (el.value) {
        el.value.textContent = props.text;
    }
});

watch(
    () => props.text,
    (text) => {
        if (el.value && document.activeElement !== el.value && el.value.textContent !== text) {
            el.value.textContent = text;
        }
    },
);
</script>
